import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Container, Steps, TabsPanel, palette, SimpleLoader } from '@dev/ee-components';
import { safelyDecodeBase64Url } from '@dev/ee-tools';
import { lowerCase } from 'lodash-es';
import { useGetDefaultSender } from '@/components/CampaignCreator/hooks/useGetDefaultSender';
import { CampaignCreatorType, CampaignsPaths } from '@/services/dictionaries/Paths';
import { LoadDomains } from '@/store/domain/actions';
import { LoadValidEmails } from '@/store/validemails/actions';
import { useExitModal } from '@/services/hooks/useExitModal';
import {
    ClearPickedCampaign,
    getCampainCreatorStore,
    LoadCampaignStart,
    SelectTemplate,
    SetDefaultCampaign,
} from '@/store/campaignCreator/actions';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { usePilotPlanConfig } from '@/services/hooks/usePilotPlanConfig';
import { useAccountTrusted } from './hooks/useAccountTrusted';
import { ViewReportButton } from './ViewReportButton';
import { SuccessScreen } from './Success';
import { useGetStepsAb } from './hooks/useGetStepsAb';
import { useGetStepsRegular } from './hooks/useGetStepsRegular';

export const CampaignCreator = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { name, type, template } = useParams<{ name: string; type: string; template: string }>();
    const {
        campaign,
        isLoading,
        isSaved,
        isSuccess,
        // activeTemplateIndex
        // errors,
    } = useSelector(getCampainCreatorStore);
    const { isPilotPlan } = useCurrentPlanStatus();
    const { pilotDefaultCampaignOverride } = usePilotPlanConfig();

    const isSavedRef = React.useRef(isSaved);
    const { boot, cleanup, handleExit } = useExitModal(
        isSavedRef,
        CampaignsPaths.CampaignsTab,
        { title: t('exitcampaigncreator') },
        t('exitcreatorwithoutsaving')
    );
    const isAccountTrusted = useAccountTrusted();
    const defaultSender = useGetDefaultSender();
    const isAbCampaign = type === CampaignCreatorType.AB;

    useEffect(() => {
        dispatch(LoadValidEmails());
        dispatch(LoadDomains());
        // dispatch(GetCampaigns({ isUrlUpdateDisabled: true, limit: 0 }));
    }, []);

    useEffect(() => {
        boot();
        return () => {
            dispatch(ClearPickedCampaign());
            cleanup();
        };
    }, []);

    useEffect(() => {
        isSavedRef.current = isSaved;
    }, [isSaved]);

    useEffect((): void => {
        if (name) {
            !campaign && !isLoading && dispatch(LoadCampaignStart(safelyDecodeBase64Url(name)));
        }
    }, [name]);

    useEffect((): void => {
        if ((defaultSender?.email || defaultSender?.name) && !name) {
            dispatch(
                SetDefaultCampaign(
                    true,
                    isAccountTrusted,
                    defaultSender.email ?? '',
                    defaultSender.name ?? '',
                    isAbCampaign,
                    isPilotPlan && pilotDefaultCampaignOverride
                )
            );
            if (template) {
                const templateName = safelyDecodeBase64Url(template);
                dispatch(SelectTemplate(templateName, false, true));
            }
        }
    }, [defaultSender, isPilotPlan]);

    const stepsRegular = useGetStepsRegular();
    const stepsAb = useGetStepsAb(isAbCampaign);

    if (isLoading) {
        return (
            <Container>
                <SimpleLoader />
            </Container>
        );
    }

    return (
        <Container>
            {isSuccess ? (
                <SuccessScreen campaign={campaign} />
            ) : (
                <>
                    <TabsPanel
                        tabBackground={palette.basic.paper}
                        breadcrumbs={t('campaigns')}
                        breadcrumbsOnClick={() => handleExit(null, true)}
                        title={
                            isAbCampaign
                                ? `${t('alphabetsignA')}/${t('alphabetsignB')} ${lowerCase(t('campaign'))}`
                                : t('regularcampaign')
                        }
                        isMobile={false}
                        additionalHeaderContent={<ViewReportButton />}
                        disableSentenceCase
                    />
                    <Steps isTabsView={isAbCampaign} steps={isAbCampaign ? stepsAb : stepsRegular} />
                </>
            )}
        </Container>
    );
};
